import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { observer } from 'mobx-react';

import Card from '@/comp/pages/ValueDashboardPage/Card';
import { useStores } from '@/hooks/use-stores';
import { getDynamicComponent } from '@/constants/componentsMap';
import { ComponentTierName } from '@/constants/enums';
import { viewPdfInWindow } from '@/utils/downloadAndViewUtils';

import { InsightsContainer } from './styled';
import OverviewTab from './Overview/OverviewTab';
import ReportIcon from '@/assets/Report.svg';

export const activeTabs = {
  overview: 'overview',
  events: 'events',
};

const Insights = observer(({
  className,
  title,
  description,
  animation = true,
  tabKey,
  pendingTasksOnly,
  eventsLimit,
}: {
  className: string;
  title?: string;
  description?: React.JSX.Element | string;
  animation?: boolean;
  tabKey?: 'overview' | 'events';
  pendingTasksOnly?: boolean;
  eventsLimit?: number;
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(activeTabs.overview);
  const [isTabBarAnimated, setIsTabBarAnimated] = useState(true);
  const { insightsStore, stationStore, reportStore } = useStores();

  const handleViewReport = useCallback(async () => {
    const assetIdKey = reportStore?.latestPublishedReport?.assetIdKey;
    if (!assetIdKey) return;

    const reportTitle = reportStore?.latestPublishedReport?.title || 
                        reportStore?.latestPublishedReport?.reportName || 
                        t('Periodic report');

    await viewPdfInWindow({
      url: assetIdKey,
      title: reportTitle,
      loadingText: t('loadingDocument'),
    });
  }, [reportStore?.latestPublishedReport?.assetIdKey, reportStore?.latestPublishedReport?.title, reportStore?.latestPublishedReport?.reportName, t]);
  const currentTier = stationStore?.stationTier;
  const EventsTab = getDynamicComponent(ComponentTierName.Insights, currentTier) as React.ComponentType<{
    activeTab: string;
  }> | undefined;
  if (eventsLimit) {
    insightsStore.setEventsLimit(eventsLimit);
  }

  useEffect(() => {
    if (tabKey) {
      setIsTabBarAnimated(false);
      setActiveTab(tabKey);
      if (tabKey === activeTabs.events) {
        insightsStore.setPendingTasksOnly(pendingTasksOnly);
      }
    }
  }, [tabKey]);

  useEffect(() => {
    const stationId = stationStore?.currentStation?.id;
    if (stationId) {
      reportStore.getLatestPublishedReport();
    }
  }, [stationStore?.currentStation?.id]);

  const tabItems = [
    {
      label: t('overview'),
      key: activeTabs.overview,
      children: <OverviewTab activeTab={activeTab} animation={animation} />,
    },
    {
      label: t('events'),
      key: activeTabs.events,
      children: EventsTab ? <EventsTab activeTab={activeTab} /> : null,
    },
  ];

  return (
    <InsightsContainer className={className}>
      <div className="display-flex">
        <h1>{title ?? t('insights')}</h1>
      </div>
      {description && <div className="description">{description}</div>}
      <div className="insights-card-wrapper">
        <Card className="insights-card">
          <Suspense>
            <Tabs
              onAnimationEnd={() => setIsTabBarAnimated(true)}
              activeKey={activeTab}
              onChange={setActiveTab}
              animated={{
                inkBar: isTabBarAnimated,
                tabPane: false,
              }}
              items={tabItems}
            />
          </Suspense>
          {reportStore.latestPublishedReport && reportStore.canOnlyViewPeriodicReports && (
            <footer className="insights-card-footer">
              <article className="periodic-report-card">
                <img className="periodic-report-icon" src={ReportIcon} alt="Report" width={24} height={24} />
                <hgroup className="periodic-report-content">
                  <h3 className="periodic-report-title">
                    {reportStore.latestPublishedReport.title || 
                     reportStore.latestPublishedReport.reportName || 
                     t('Periodic report')}
                  </h3>
                  <p className="periodic-report-details">
                    {reportStore.latestPublishedReport.startDate && 
                     reportStore.latestPublishedReport.endDate && (
                      <span className="periodic-report-date-range">
                        {moment(reportStore.latestPublishedReport.startDate).format('MMM D, YYYY')} - {moment(reportStore.latestPublishedReport.endDate).format('MMM D, YYYY')}
                      </span>
                    )}
                    {reportStore.latestPublishedReport.estimatedValue && (
                      <>
                        <span className="periodic-report-separator" />
                        <span className="periodic-report-label">{t('Potential Value')}</span>
                        <span className="periodic-report-value">
                          ${reportStore.latestPublishedReport.estimatedValue.toLocaleString()}
                        </span>
                      </>
                    )}
                  </p>
                </hgroup>
                <button
                  className="periodic-report-view-button"
                  onClick={handleViewReport}
                >
                  {t('viewReport')}
                </button>
              </article>
            </footer>
          )}
        </Card>
        
      </div>
    </InsightsContainer>
  );
});

export default Insights;
